import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import { CallSplitRounded } from '@mui/icons-material';
import { useJourneyStep } from 'api/Journeys/journey_queries';
import { flowStateChip, StepTypeIcon } from 'pages/Journeys/JourneyConstants';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'contexts/AuthContext';
import { enqueueSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';

const MergeSidebar = ({ stepId: journeyStepId, hideSidebar, onCanDeleteChange }) => {
    const { id } = useParams();
    const journeyId = parseInt(id);
    const { axiosElementalApiCurrentOrg } = useAuth();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    
    const { data: journeyStep, error, isFetching } = useJourneyStep(journeyId, journeyStepId);

    console.log('MergeSidebar', journeyStep);

    // Function to find steps that merge into this merge step
    const findStepsMergingIntoThis = (adjacencyList) => {
        const mergingSteps = [];
        adjacencyList.forEach((value) => {
            const step = value.step;
            const nextSteps = value.next_steps || [];
            
            // Check if any next step is our target merge step
            nextSteps.forEach((nextStep) => {
                if (nextStep.step.id === parseInt(journeyStepId)) {
                    mergingSteps.push({
                        step: step,
                        edge: nextStep.edge
                    });
                }
            });
        });
        return mergingSteps;
    };

    // Get adjacency list to find merging steps
    const { data: mergingSteps, isLoading: isLoadingMergingSteps } = useQuery({
        queryKey: ['journeys', journeyId, 'adjacencyList', 'draft'],
        queryFn: async () => {
            return axiosElementalApiCurrentOrg.get(`/flows/${journeyId}/draft/adjacency_list`);
        },
        select: findStepsMergingIntoThis,
        enabled: !!journeyId && !!journeyStepId
    });

    // Notify parent about delete state
    useEffect(() => {
        if (onCanDeleteChange && mergingSteps !== undefined) {
            const canDelete = mergingSteps.length <= 1;
            const popoverMessage = canDelete 
                ? null 
                : 'Unmerge all but a single step and then you can delete this step';
            
            onCanDeleteChange({
                can_delete: canDelete,
                popover_message: popoverMessage
            });
        }
    }, [mergingSteps, onCanDeleteChange]);

    // Mutation to unmerge a step
    const unmergeMutation = useMutation({
        mutationFn: (unmergeData) => {
            // @ts-ignore
            const stepId = unmergeData.stepId;
            // @ts-ignore
            const keyFrom = unmergeData.keyFrom;
            const payload = keyFrom ? { key_from: keyFrom } : {};
            return axiosElementalApiCurrentOrg.put(`/flows/${journeyId}/${stepId}/unmerge`, payload);
        },
        onSuccess: () => {
            // Refresh the adjacency list and journey data
            // queryClient.invalidateQueries({ queryKey: ['journeys', journeyId, 'adjacencyList', 'draft'] });
            queryClient.invalidateQueries({ queryKey: ['journey_step', journeyStepId] });
            queryClient.invalidateQueries({ queryKey: ['journeys', journeyId] });
            enqueueSnackbar('Step unmerged successfully');
        },
        onError: (error) => {
            enqueueSnackbar('Failed to unmerge step', { variant: 'error' });
            console.error('Unmerge error:', error);
        }
    });

    const handleUnmerge = (fromStepId, keyFrom) => {
        confirm({
            title: 'Confirm Unmerge',
            description: `Are you sure you want to unmerge. This will remove the connection to this merge and connect the step to an exit instead`,
            confirmationText: 'Unmerge',
            confirmationButtonProps: { color: 'error' },
            dialogProps: {
                sx: {
                    zIndex: 1450,
                    '& .MuiDialog-paper': {
                        zIndex: (theme) => 1450 + 1,
                    }
                }
            }
        }).then(() => {
            // @ts-ignore
            unmergeMutation.mutate({ stepId: fromStepId, keyFrom: keyFrom });
        }).catch(() => {
            // User cancelled the action
        });
    };

    if (isFetching) {
        return (
            <Box>
                <Typography>Loading merge step...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Typography color="error">Error loading merge step</Typography>
            </Box>
        );
    }

    if (!journeyStep) {
        return (
            <Box>
                <Typography>No merge step found</Typography>
            </Box>
        );
    }

    const statusBadge = flowStateChip(journeyStep.status, journeyStep.has_changes, 'small');

    return (
        <Box className='flow-sidebar__body' p={0}>
            <Box className='flow-sidebar__body-content'>
                <Box display='flex' flexDirection='column'>
                    <Box display='flex' flexDirection='column' p={1} className='merge__step'>
                        <Box display='flex' justifyContent='space-between' style={{width: '100%', alignItems: 'center'}}>
                            <Typography variant='subtitle1' style={{display: 'inline-flex', alignItems: 'center'}}>
                                Merge <span style={{marginLeft: 10}}>{statusBadge}</span>
                            </Typography>
                        </Box>
                        
                        <Box mb={2} mt={1}>
                            <Typography variant="body2" color="textSecondary">
                                {journeyStep.description || 'No description'}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Merged Steps
                            </Typography>
                            
                            {isLoadingMergingSteps ? (
                                <Typography variant="body2" color="textSecondary">
                                    Loading merged steps...
                                </Typography>
                            ) : mergingSteps && mergingSteps.length > 0 ? (
                                <List dense>
                                    {mergingSteps.map(({ step, edge }) => (
                                        <ListItem
                                            key={`${step.id}-${edge.id}`}
                                            sx={{ 
                                                border: '1px solid #e0e0e0', 
                                                borderRadius: 1, 
                                                mb: 1,
                                                backgroundColor: '#fafafa'
                                            }}
                                            secondaryAction={
                                                mergingSteps.length > 1 ? (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        startIcon={<CallSplitRounded style={{ transform: 'rotate(180deg)' }} />}
                                                        onClick={() => handleUnmerge(step.id, edge.key_from)}
                                                        disabled={unmergeMutation.isLoading}
                                                    >
                                                        Unmerge
                                                    </Button>
                                                ) : null
                                            }
                                        >
                                            <ListItemIcon>
                                                <StepTypeIcon step={step} color={undefined} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={step.description || step.label || `Step ${step.id}`}
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" component="span">
                                                            ID: {step.id} | Type: {step.type}
                                                        </Typography>
                                                        {edge.key_from && (
                                                            <Typography variant="body2" component="span" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                                                                | Branch: {edge.key_from}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No steps are currently merged into this merge step.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MergeSidebar;
